import dayjs from "dayjs";
import { postBooking } from "../../../app/api/postBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { uuid } from "../../../core/utils/Uuid";
import { calculatePrice, determinePassengerType, getUpperPassengerLabelByAge } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse, IBookedOffer, IBooking, IBookingPassenger, IBookingTypeTrip, IDeterminePassenger, ISelectedPassenger, OffersRequest, PassengerSpecification } from "../../../Orchestrator/Types/Index";
import { IGetLegsFromTripActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/TrainActions";
import { BookingTypeTrip, IBookingRequest, IBookingResponse } from "../../../Orchestrator/Types/Types/BookingTypes";
import { getLegsSummaryFromTripAction } from "../../UIActions/TrainOffer/GetLegsFromTripAction";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getBooking } from "../../../app/api/getBooking";
import { isShuttleOffer, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";


class GetProcessBookingInitAction extends BaseAction<void, void> {

    getAllOfferWithoutExchange(bookedOffers: IBookedOffer[]) {
        const allBookedOfferWithoutExchange: IBookedOffer[] = []
        bookedOffers?.forEach((offer: IBookedOffer) => {
            const offerAdmissions = offer.admissions;
            const isAnyFulfilled = offerAdmissions.find(d => d.status === "EXCHANGED" || d.status === "EXCHANGE_ONGOING")
            if (!isAnyFulfilled) {
                allBookedOfferWithoutExchange.push(offer)
            }
        })
        return allBookedOfferWithoutExchange
    }
    getAllTripWithoutExchange(bookedOffers: IBookedOffer[], trips: BookingTypeTrip[]) {
        const allTripCoverageIds = bookedOffers.map(offer => offer?.tripCoverage?.coveredTripId)
        const allTripWithoutExchange: BookingTypeTrip[] = trips.filter(trip => allTripCoverageIds.includes(trip.id))
        return allTripWithoutExchange
    }

    // isShuttleOffer, not `products[0].code === SHUTTLE_PRODUCT_CODE` — that
    // constant is "SHTL" while the wire code is "SHTL_ADM", so the comparison
    // matched nothing and no shuttle was ever stripped. See the note in
    // CalculatePassengerListByPriceAction for what that cost.
    getShuttleTripIds(bookedOffers: any) {
        return bookedOffers
            .filter((offer: any) => isShuttleOffer(offer))
            .map((offer: any) => offer?.tripCoverage?.coveredTripId);
    }

    getPassengerListByOfferAdmissions = async (
        passengers: ISelectedPassenger[],
        allTrip: IBookingTypeTrip[],
        bookedOffers: IBookedOffer[]
    ) => {
        const passengerList: IBookingPassenger = {
            outbound: [],
            inbound: []
        }
        const outBoundTrips = allTrip.filter(i => i.direction === TripTypeEnum.OUT_BOUND)
        const inBoundTrips = allTrip.filter(i => i.direction === TripTypeEnum.IN_BOUND)

        const outBoundTripIds = outBoundTrips.map(trip => trip.id)
        const outBoundOffers = bookedOffers.filter(offer => outBoundTripIds.includes(offer?.tripCoverage?.coveredTripId))
        
        if (outBoundOffers && outBoundOffers.length > 0) {
            const allTheOutBoundPassengerId = Array.from(new Set(
                outBoundOffers.flatMap(off => (off?.admissions || []).flatMap(add => add.passengerIds || []))
            ))
            
            passengerList.outbound = JSON.parse(JSON.stringify([...passengers]))
                .filter((d: ISelectedPassenger) => allTheOutBoundPassengerId.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheOutBoundPassengerId.includes(pass.id)) {
                        const admissionsForPassenger = outBoundOffers.flatMap(off => 
                            (off?.admissions || []).filter(add => add.passengerIds?.includes(pass.id))
                        )
                        if (admissionsForPassenger.length > 0) {
                            const totalPrice = admissionsForPassenger.reduce((sum: number, add: any) => 
                                sum + calculatePrice(add.price), 0
                            )
                            pass.passengerPrice = totalPrice
                            pass.passengerStatus = admissionsForPassenger[0].status
                        }
                    }
                    return pass
                })
        }

        const inBoundTripIds = inBoundTrips.map(trip => trip.id)
        const inBoundOffers = bookedOffers.filter(offer => inBoundTripIds.includes(offer?.tripCoverage?.coveredTripId))
        
        if (inBoundOffers && inBoundOffers.length > 0) {
            const allTheInBoundPassengerId = Array.from(new Set(
                inBoundOffers.flatMap(off => (off?.admissions || []).flatMap(add => add.passengerIds || []))
            ))
            
            passengerList.inbound = JSON.parse(JSON.stringify([...passengers]))
                .filter((d: ISelectedPassenger) => allTheInBoundPassengerId.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheInBoundPassengerId.includes(pass.id)) {
                        const admissionsForPassenger = inBoundOffers.flatMap(off => 
                            (off?.admissions || []).filter(add => add.passengerIds?.includes(pass.id))
                        )
                        if (admissionsForPassenger.length > 0) {
                            const totalPrice = admissionsForPassenger.reduce((sum: number, add: any) => 
                                sum + calculatePrice(add.price), 0
                            )
                            pass.passengerPrice = totalPrice
                            pass.passengerStatus = admissionsForPassenger[0].status
                        }
                    }
                    return pass
                })
        }
        
        return passengerList
    }

    updatePaymentItems = (outBoundPassengerPriceList: any, atomKey: string) => {
        let allOutBoundInfantCount = 0
        let allOutBoundChildrenCount = 0
        let allOutBoundAdultCount = 0
        let allStudentCount = 0
        let allOutBoundSeniorCitizenCount = 0
        let allWheelchairCount = 0
        let allReducedMobilityCount = 0
        outBoundPassengerPriceList.forEach((p: any) => {
            if (p.pType === "Infant") allOutBoundInfantCount++
            if (p.pType === "Children") allOutBoundChildrenCount++
            if (p.pType === "Student") allStudentCount++
            if (p.pType === "Adult") allOutBoundAdultCount++
            if (p.pType === "Senior Citizen") allOutBoundSeniorCitizenCount++
            if (p.pType === "Wheelchair") allWheelchairCount++
            if (p.pType === "People with Reduced Mobility") allReducedMobilityCount++
        })
        const outBoundPassengerDetails: any = []


        const addPassengerDetail = (count: number, pType: string, label: string) => {
            if (count > 0) {
                const totalAmount = outBoundPassengerPriceList
                    .filter((d: any) => d.pType === pType)
                    .reduce((sum: number, d: any) => sum + (d.pValue || 0), 0);

                outBoundPassengerDetails.push({
                    description: `${count} ${label}`,
                    amount: totalAmount
                });
            }
        };

        // Use common function to handle all types
        addPassengerDetail(allOutBoundAdultCount, "Adult", "Adult");
        addPassengerDetail(allOutBoundChildrenCount, "Children", "Children");
        addPassengerDetail(allOutBoundInfantCount, "Infant", "Infant");
        addPassengerDetail(allOutBoundSeniorCitizenCount, "Senior Citizen", "Senior Citizen (UAE)");
        addPassengerDetail(allStudentCount, "Student", "Student");
        addPassengerDetail(allReducedMobilityCount, "People with Reduced Mobility", "People with Reduced Mobility");
        addPassengerDetail(allWheelchairCount, "Wheelchair", "Wheelchair");

        if (atomKey === "paymentOutboundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentOutboundRefundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentInboundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
        if (atomKey === "paymentInboundRefundAtom")
            updateRecoilState(atomKey, outBoundPassengerDetails)
    }

    calculatePassengerListByPrice = async (booking: IBooking) => {
        //Calculate passenger price        
        let allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(booking.bookedOffers)
        const allTripWithoutExchange: BookingTypeTrip[] = this.getAllTripWithoutExchange(allBookedOfferWithoutExchange, booking.trips)
        const trips = allTripWithoutExchange.filter(trip => {
            const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
            return !(shuttleTripIds.includes(trip.id) && (trip.legs.length === trip.legs.filter(leg => leg.timedLeg.service.mode.ptMode === "BUS").length))
        });
        const shuttleTrips = allTripWithoutExchange.filter(trip => {
            const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
            return shuttleTripIds.includes(trip.id);
        });

        const filteredOffers = allBookedOfferWithoutExchange.filter(
            offer => offer.products.length > 0 && !isShuttleOffer(offer)
        );
        allBookedOfferWithoutExchange = filteredOffers.length === 0 ? allBookedOfferWithoutExchange : filteredOffers;

        const outBoundPassengerPriceList: any = []
        const inBoundPassengerPriceList: any = []

        const updatedPassenger: ISelectedPassenger[] = booking.passengers.map((pass) => {
            const selectedPass: ISelectedPassenger = {
                email: pass?.detail?.email || "",
                firstName: pass?.detail?.lastName || "",
                id: pass.id,
                lastName: pass?.detail?.lastName || "",
                mobile: "",
                remember: pass.remember || false,
                age: pass.age,
                seatingDetails: null,
                cards: pass.cards || [],
                prmNeeds: pass.prmNeeds || [],
            }
            return selectedPass
        })
        const shuttleTripsOrTrips = trips.length === 0 ? shuttleTrips : trips
        const allPassenger = await this.getPassengerListByOfferAdmissions(updatedPassenger, shuttleTripsOrTrips, allBookedOfferWithoutExchange)

        allPassenger.outbound.forEach(pass => {
            const priceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pStatus: pass.passengerStatus
            }
            outBoundPassengerPriceList.push(priceDetails)
        })
        allPassenger.inbound.forEach(pass => {
            const priceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pStatus: pass.passengerStatus
            }
            inBoundPassengerPriceList.push(priceDetails)
        })
        // updateRecoilState("allPassengerPriceAndAgeGroup", outBoundPassengerPriceList)
        // updateRecoilState("allPassengerPriceAndAgeGroupInBound", inBoundPassengerPriceList)

        //PREBOOKED means this function called from processBooking function
        const allTheFulFillmentPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED")
        const allTheRefundedPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")
        this.updatePaymentItems(allTheFulFillmentPassengers, "paymentOutboundAtom")
        this.updatePaymentItems(allTheRefundedPassengers, "paymentOutBoundRefundItemsDetails")


        //PREBOOKED means this function called from processBooking function
        const allTheFulFillmentPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED")
        const allTheRefundedPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")
        this.updatePaymentItems(allTheFulFillmentPassengersInBound, "paymentInboundAtom")
        this.updatePaymentItems(allTheRefundedPassengersInBound, "paymentOutBoundRefundItemsDetailsInBoundAtom")

        return { outBoundPassengerPriceList, inBoundPassengerPriceList }
    }

    async getGuestBooking(bookingId: string) {
        // // Prepare the booking parameters        
        // const passengerRefs: string[] = [];
        // const passengerSpecifications: PassengerSpecification[] = [];

        // offersRequest.anonymousPassengerSpecifications.forEach((element: any) => {
        //     passengerRefs.push(element.externalRef);
        //     passengerSpecifications.push({
        //         externalRef: element.externalRef,
        //         detail: null,
        //         type: element.type,
        //         age: element.age,
        //         cards: (element?.cards || []).length > 0 ? element.cards : [],
        //         ...(element.prmNeeds?.length > 0 ? { prmNeeds: element.prmNeeds } : {})
        //     });
        // });
        // const bookingParams = {
        //     offers: offerIds.map((offerId: any) => ({
        //         offerId: offerId,
        //         passengerRefs: passengerRefs,
        //     })),
        //     passengerSpecifications: passengerSpecifications,
        //     externalRef: uuid(),
        // };
        // // Construct the IBookingRequest object from the prepared bookingParams
        // const newBookingRequest: IBookingRequest = {
        //     offers: bookingParams.offers,
        //     passengerSpecifications: bookingParams.passengerSpecifications,
        //     externalRef: bookingParams.externalRef,
        // };

        // // Update the booking request state in Recoil
        // updateRecoilState<IBookingRequest>('bookingRequestAtom', newBookingRequest);

        try {
            // Clear the booking response atom before making the request
            updateRecoilState('bookingResponseAtom', null);
            // Post booking data to the API
            const response: IApiResponse<IBookingResponse> = await getBooking(bookingId);
            if (response.status === 'ok' && response.answer) {

                const bookingResponse: IBookingResponse = response.answer;
                // Update Recoil state with the booking response                

                updateRecoilState('bookingResponseAtom', bookingResponse);

                const isAddShuttleFlow = localStorage.getItem("isAddShuttleTransferFlow") === "true";
                // On /checkout (re-)entry in manage-seating — including
                // cancel-back from the gateway — flag the call so
                // PrepareBookingSummary takes the manage-seating branch
                // (clears passenger fare breakdown, applies addon-bucketing
                // logic for pendingRefund/canceled/active). Without this,
                // the regular-checkout branch runs and the receipt re-
                // computes ticket fare as a new charge ("1 Adult ₿59")
                // and the deselected ancillary stays tagged as "(Canceled)"
                // / "Refunded" instead of "(Refund)" / "To Refund".
                const isManageSeatingFlow =
                    (getRecoilStateValue("isManageSeatingAtom") as boolean) || false;
                prepareBookingSummaryAction.setParams({
                    ...bookingResponse.booking,
                    ...(isAddShuttleFlow && { isPrebookedOnly: true }),
                    ...(isManageSeatingFlow && { isManageSeating: true }),
                })
                await prepareBookingSummaryAction.execute()

                await this.calculatePassengerListByPrice(bookingResponse.booking)

                const passengers = bookingResponse.booking.passengers
                const counts: Record<string, number> = {};
                passengers.forEach(passenger => {
                    const determinePassenger: IDeterminePassenger = {
                        age: passenger?.age,
                        cards: passenger?.cards || [],
                        prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                    }
                    const type = determinePassengerType(determinePassenger);
                    counts[type] = (counts[type] || 0) + 1;
                });
                
                const orderedPassengerTypes = [
                    "adults",
                    "children",
                    "infants",
                    "seniorcitizens",
                    "students",
                    "reducedMobility",
                    "wheelchair"
                ];

                // Create a new object with keys in the correct order
                const orderedCounts: any = {};
                orderedPassengerTypes.forEach(type => {
                    if (counts[type] !== undefined) {
                        orderedCounts[type] = counts[type];
                    }
                });
                updateRecoilState("searchStateAtom", { passengers: orderedCounts });
                // updateRecoilState("searchStateAtom", { passengers: counts });
            }
        } catch (error: any) {
            console.error('Error processing booking:', error);
        }
    }

    override async run(): Promise<void> {
        try {
            const guestCheckoutBookingId = sessionStorage.getItem("guestCheckoutBookingId");
            if (guestCheckoutBookingId) {
                // Flags first, THEN the booking fetch. getGuestBooking runs
                // PrepareBookingSummary, whose quick-exchange total block zeroes
                // payment.total unless isAddShuttleFlowAtom excludes it — and
                // isQuickExchangeStateAtom is persisted while isAddShuttleFlowAtom is
                // not, so on this /checkout recovery path QE can read true on its own.
                // The old order only worked because the unawaited getGuestBooking
                // suspends on its getBooking before reaching the summary; one added
                // `await` would have turned that into a free shuttle.
                if (localStorage.getItem("isAddShuttleTransferFlow") === "true") {
                    updateRecoilState("isAddShuttleFlowAtom", true);
                    updateRecoilState("isQuickExchangeStateAtom", true);
                }

                this.getGuestBooking(guestCheckoutBookingId)
                // Clear the pending booking data from session storage
                sessionStorage.removeItem("guestCheckoutBookingId");
            }
        } catch (error: any) {

        }
    }
}

export const getProcessBookingInitAction = new GetProcessBookingInitAction()